import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  APPLICANT_TYPE_CHOICES,
  getApplicantType,
  setApplicantType,
  type ApplicantOperatorType,
} from "../../api/applicant";

/**
 * "What kind of operator are you?" -- the first thing a newly registered
 * operator answers after signing in (owner 2026-10-02).
 *
 * Shown on the Welcome page to a login that holds `applicant` and no operator
 * role. The answer is saved on the applicant's own sign-up; a reviewer sees it
 * at approval. It can be changed until the sign-up is reviewed.
 */
export function OperatorTypeQuestion() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["applicant", "type", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getApplicantType(identity, ns)).data,
  });
  const [choice, setChoice] = useState<ApplicantOperatorType | "">("");
  useEffect(() => {
    if (q.data) setChoice(q.data.operatortype);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async (t: ApplicantOperatorType) => (await setApplicantType(identity, ns, t)).data,
    onSuccess: (d) => qc.setQueryData(["applicant", "type", ns], d),
  });

  const saved = q.data?.operatortype ?? "";
  const editable = q.data?.editable ?? false;
  const dirty = choice !== "" && choice !== saved;

  return (
    <Box
      sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}
      data-testid="operator-type-question"
    >
      <Typography variant="h6" sx={{ fontSize: 18 }}>
        What kind of operator are you?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This decides what you may sell and who issues the invoices. Ankh checks it when your
        registration is reviewed.
      </Typography>

      {q.isLoading ? (
        <Skeleton variant="rectangular" height={160} />
      ) : q.error ? (
        <Alert severity="error">
          {q.error instanceof ApiError && q.error.kind === "not-found"
            ? "Your sign-up could not be found for this login. Contact Ankh support."
            : (q.error as Error).message}
        </Alert>
      ) : (
        <>
          <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value as ApplicantOperatorType)}>
            {APPLICANT_TYPE_CHOICES.map((c) => (
              <FormControlLabel
                key={c.value}
                value={c.value}
                disabled={!editable || save.isPending}
                control={<Radio />}
                sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0.25 } }}
                label={
                  <Box>
                    <Typography variant="body1">{c.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.detail}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {!editable && (
            <Alert severity="info">Your registration has been reviewed, so this answer can no longer be changed here.</Alert>
          )}
          {save.isError && <Alert severity="error">{(save.error as Error).message}</Alert>}
          {save.isSuccess && !dirty && <Alert severity="success">Saved.</Alert>}
          {editable && (
            <Box>
              <Button variant="contained" disabled={!dirty || save.isPending} onClick={() => choice && save.mutate(choice)}>
                {save.isPending ? "Saving…" : saved ? "Change answer" : "Save answer"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
